/**
 * CancelSubscriptionDialog Component
 *
 * Confirmation dialog for subscription cancellation.
 * Shows effective date and warns if <7 days until period end.
 */

import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/v2/ui/Dialog'
import { Button } from '@/components/v2/ui/Button'

interface CancelSubscriptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  periodEnd: string
  onConfirm: () => void
  loading: boolean
}

function daysUntil(dateStr: string): number {
  const end = new Date(dateStr)
  const now = new Date()
  const diff = end.getTime() - now.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  periodEnd,
  onConfirm,
  loading,
}: CancelSubscriptionDialogProps) {
  const { t } = useTranslation()
  const formattedDate = new Date(periodEnd).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const remaining = daysUntil(periodEnd)
  const isUrgent = remaining >= 0 && remaining < 7

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('billing.cancelSubscription')}</DialogTitle>
          <DialogDescription>
            {t('billing.cancelConfirmDescription', { date: formattedDate })}
          </DialogDescription>
        </DialogHeader>

        {isUrgent && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('billing.urgentCancellation')}</span>
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={loading}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={loading}
          >
            {t('billing.confirmCancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
